import {
    Component,
    Event,
    Element,
    EventEmitter,
    h,
    Host,
    Listen,
    Method,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { debounce } from "../../../utils/debounce";
import { limitSlotElements } from "../../../utils/limitSlotElements";
import {
    focusNextChild,
    getLevel,
    handleArrowNavigation,
    isFocusable,
} from "../helpers";

/**
 * This component is used to display a menu item inside a menu.
 * @slot icon - Slot for an optional icon (`<ux-icon>`)
 * @slot (default) - The default slot is reserved for submenu items (`<ux-iws-sf-menu-item>`)
 */
@Component({
    tag: "ux-iws-sf-menu-item",
    styleUrl: "sf-menu-item.scss",
    shadow: true,
})
export class SfMenuItem {
    private static uidIncrement = 0;
    private uid = `ux-iws-sf-menu-item-${SfMenuItem.uidIncrement++}`;
    private hasSubmenu: boolean = false;

    /** The host element of the component */
    @Element() host!: HTMLUxIwsSfMenuItemElement;

    /** Label of the menu item */
    @Prop() label: string = "";

    /** Level of the menu item (internal use, detected automatically) */
    @Prop({ mutable: true, reflect: true }) level: number = 0;

    /** Name of the menu item used to identify it (e.g. a slug, path or URL) */
    @Prop({ reflect: true }) name: string = "";

    /** Indicates that the menu item is currently active */
    @Prop({ reflect: true }) current: boolean = false;

    /** Indicates that the current item is inside the submenu (internal use, detected automatically) */
    @Prop({ mutable: true, reflect: true }) currentChild: boolean = false;

    /** If `expanded` is `true`, the children are shown */
    @Prop({ mutable: true, reflect: true }) expanded: boolean | undefined =
        undefined;

    /** Controls the `tabindex`  of the menu item */
    @State() focusable: boolean = true;

    /** Event that is emitted when a menu item is clicked */
    @Event({
        eventName: "uxMenuItemClick",
        bubbles: true,
        cancelable: true,
    })
    menuItemClickEvent!: EventEmitter<{
        label: string;
        name: string;
    }>;

    /** Event that is emitted when the menu item is expanded */
    @Event({
        eventName: "uxMenuItemExpand",
        bubbles: true,
        cancelable: true,
    })
    submenuShowEvent!: EventEmitter<{
        label: string;
        name: string;
    }>;

    @Listen("uxMenuItemClick", { target: "document" })
    handleMenuItemClick() {
        this.update();
    }

    @Listen("uxMenuItemExpand", { target: "document" })
    handleMenuItemExpand() {
        this.update();
    }

    @Watch("expanded")
    handleExpandedChange() {
        this.update();
    }

    /** Update state debounced */
    private update = debounce(() => {
        this.currentChild = this.getCurrentChild();
        this.focusable = isFocusable(this.host);
    }, 40);

    /** Checks if the menu item has a submenu */
    private getHasSubmenu = (): boolean => {
        return !!this.host.querySelector(`ux-iws-sf-menu-item`);
    };

    /** Checks if a submenu item is current */
    private getCurrentChild = (): boolean => {
        return !!this.host.querySelector(`ux-iws-sf-menu-item[current]`);
    };

    /** Focuses the current or first visible menu item */
    private focusFirstItem = debounce(async () => {
        focusNextChild(this.host);
    }, 0);

    /** Expands the menu item and focuses the first submenu item. */
    @Method()
    async expand() {
        this.hasSubmenu = this.getHasSubmenu();
        if (!this.hasSubmenu) {
            return;
        }
        if (!this.expanded) {
            this.expanded = true;
            this.submenuShowEvent.emit();
            this.focusFirstItem();
        }
    }

    /** Sets focus on the menuitem element */
    @Method()
    async setFocus() {
        this.host.shadowRoot
            ?.querySelector<HTMLButtonElement>("#button")
            ?.focus();
    }

    componentWillLoad() {
        this.hasSubmenu = this.getHasSubmenu();
        this.currentChild = this.getCurrentChild();
        this.level = getLevel(this.host);
        this.expanded = this.expanded === true;
        this.focusable = isFocusable(this.host);

        limitSlotElements(this.host, {
            allowed: {
                icon: ["ux-icon", "svg"],
                "": ["ux-iws-sf-menu-item", "hr"],
            },
        });
    }

    componentDidLoad() {
        this.update();
    }

    /**
     * Handles the click event on the menu item button.
     * Emits the `uxMenuItemClick` event.
     * If the menu item has a submenu, it toggles the `expanded` state.
     * If the submenu is expanded, it emits the `uxMenuItemExpand` event.
     */
    private handleClick = (event: Event) => {
        if (this.hasSubmenu) {
            this.expanded = !this.expanded;
            if (this.expanded) {
                this.submenuShowEvent.emit({
                    label: this.label,
                    name: this.name,
                });
            }
            if ((event.target as HTMLElement).closest("ux-icon")) {
                // The expand icon was clicked
                return;
            }
        }
        this.menuItemClickEvent.emit({
            label: this.label,
            name: this.name,
        });
    };

    /**
     * Handles keydown events for the menu item.
     * Allows navigation with arrow keys and focuses the next or previous menu item.
     * @param event - The keyboard event.
     */
    private handleKeyDown = async (event: KeyboardEvent) => {
        handleArrowNavigation(event, this.host);
    };

    render() {
        this.hasSubmenu = this.getHasSubmenu();
        const hasIcon =
            Array.from(this.host.childNodes).filter((el) =>
                el instanceof HTMLElement ? el.slot === "icon" : false
            ).length > 0;
        return (
            <Host role="presentation">
                <button
                    id="button"
                    role="menuitem"
                    aria-label={this.label}
                    aria-haspopup={this.hasSubmenu ? "true" : "false"}
                    aria-expanded={
                        this.hasSubmenu
                            ? this.expanded
                                ? "true"
                                : "false"
                            : undefined
                    }
                    aria-controls={
                        this.hasSubmenu ? `${this.uid}-submenu` : undefined
                    }
                    onKeyDown={this.handleKeyDown}
                    onClick={this.handleClick}
                    tabindex={this.focusable ? 0 : -1}
                >
                    <div id="icon" class={hasIcon ? undefined : "is-hidden"}>
                        <slot name="icon" />
                    </div>
                    <div id="label">{this.label}</div>
                    <ux-icon
                        name={
                            this.hasSubmenu
                                ? `chevron-${this.expanded ? "up" : "down"}`
                                : "chevron-right"
                        }
                        weight="bold"
                        aria-hidden="true"
                    />
                </button>
                <div
                    class="submenu"
                    id={`${this.uid}-submenu`}
                    aria-hidden={this.expanded ? "false" : "true"}
                >
                    <slot />
                </div>
            </Host>
        );
    }
}
